import type { ClientMessage } from '@harness/shared/protocol';
import { useEffect, useRef } from 'react';
import { SafeMarkdown } from '../markdown';
import type { ChatState, TimelineItem } from '../state';
import styles from '../styles/timeline.module.css';
import { doneText, TEXT } from '../texts';
import { FileList } from './FileList';
import { RequestCard } from './RequestCard';
import { ToolLine } from './ToolLine';

interface TimelineProps {
  state: ChatState;
  projectId: string;
  /** Prompts by this name are labelled "You". */
  ownName: string;
  send: (message: ClientMessage) => void;
}

type Entry = { item: TimelineItem; index: number };

/** A prompt stands alone; everything up to the next prompt belongs to one reply. */
type Group = { kind: 'prompt'; text: string; author: string | null; index: number } | { kind: 'reply'; entries: Entry[]; index: number };

function groupItems(items: TimelineItem[]): Group[] {
  const groups: Group[] = [];
  items.forEach((item, index) => {
    const last = groups[groups.length - 1];
    if (item.kind === 'prompt') groups.push({ kind: 'prompt', text: item.text, author: item.author, index });
    else if (last?.kind === 'reply') last.entries.push({ item, index });
    else groups.push({ kind: 'reply', entries: [{ item, index }], index });
  });
  return groups;
}

function Item({ item, projectId, send }: { item: TimelineItem; projectId: string; send: TimelineProps['send'] }): React.JSX.Element | null {
  switch (item.kind) {
    case 'prompt':
      // Prompts are rendered as their own bubble by the timeline.
      return null;
    case 'text':
      return (
        <div className={styles.answer}>
          <SafeMarkdown text={item.text} />
        </div>
      );
    case 'tool':
      return <ToolLine tool={item.tool} target={item.target} />;
    case 'files':
      return <FileList projectId={projectId} paths={item.paths} />;
    case 'question':
    case 'approval':
    case 'connect':
      return <RequestCard item={item} send={send} />;
    case 'end':
      return (
        <div className={item.status === 'error' ? styles.error : styles.end}>
          {doneText[item.status]}
          {item.detail !== null && item.status === 'error' && <div>{item.detail}</div>}
        </div>
      );
    case 'error':
      return <div className={styles.error}>{TEXT.invalidMessage}</div>;
  }
}

export function Timeline({ state, projectId, ownName, send }: TimelineProps): React.JSX.Element {
  const scroller = useRef<HTMLDivElement>(null);
  const groups = groupItems(state.items);
  // While Claude works on a fresh prompt there is no reply yet; the working line opens one.
  if (state.running && groups[groups.length - 1]?.kind !== 'reply') groups.push({ kind: 'reply', entries: [], index: state.items.length });
  const lastReply = groups[groups.length - 1];
  const atBottom = useRef(true);

  // Follow new content while the consultant is at the end of the timeline.
  useEffect(() => {
    const element = scroller.current;
    if (element !== null && atBottom.current) element.scrollTop = element.scrollHeight;
  });

  return (
    <div
      className={styles.scroll}
      ref={scroller}
      onScroll={(event) => {
        const element = event.currentTarget;
        atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
      }}
    >
      <div className={styles.timeline} aria-live="polite">
        {state.items.length === 0 && !state.running && (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>{TEXT.emptyChatTitle}</p>
            <p>{TEXT.emptyChat}</p>
          </div>
        )}
        {groups.map((group) =>
          // Items are only appended or closed in place, so the index of a group's first item is a stable key.
          group.kind === 'prompt' ? (
            <div key={group.index} className={styles.prompt}>
              {/* Chats stored before prompts carried their author are always the person's own. */}
              <div className={styles.who}>{group.author === null || group.author === ownName ? TEXT.you : group.author}</div>
              <div className={styles.promptText}>{group.text}</div>
            </div>
          ) : (
            <div key={group.index} className={styles.reply}>
              <div className={styles.who}>{TEXT.assistant}</div>
              {group.entries.map(({ item, index }) => (
                <Item key={index} item={item} projectId={projectId} send={send} />
              ))}
              {state.running && group === lastReply && <div className={styles.working}>{TEXT.working}</div>}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
